import type {
  AssignedPatientResponse,
  InterventionGroupDto,
  TreatmentPlanDto,
  YearPlanResponse
} from '../api/generated';
import { ECONOMICS_KEY_SEPARATOR } from '../constants/economicsConstants';
import type {
  CompletionClaimChange,
  CompletionClaimKey,
  EconomicsChangeEntry,
  EconomicsCompletionClaimNode,
  EconomicsGroupNode,
  EconomicsLeafKey,
  EconomicsLeafState,
  EconomicsPatientNode,
  EconomicsPlanNode,
  EconomicsTreeModel,
  SelectionState
} from '../types/economicsTypes';

export function serializeKey(key: EconomicsLeafKey): string {
  return [key.treatmentPlanId, key.interventionGroupId, key.patientId].join(ECONOMICS_KEY_SEPARATOR);
}

function parseKey(serialized: string): EconomicsLeafKey {
  const [treatmentPlanId, interventionGroupId, patientId] = serialized.split(ECONOMICS_KEY_SEPARATOR);
  return { treatmentPlanId, interventionGroupId, patientId };
}

export function serializeClaimKey(key: CompletionClaimKey): string {
  return [key.treatmentPlanId, key.patientId].join(ECONOMICS_KEY_SEPARATOR);
}

function parseClaimKey(serialized: string): CompletionClaimKey {
  const [treatmentPlanId, patientId] = serialized.split(ECONOMICS_KEY_SEPARATOR);
  return { treatmentPlanId, patientId };
}

// Shadow seats never appear in economics — only real patients are billable.
function realSeats(group: InterventionGroupDto): AssignedPatientResponse[] {
  return (group.assignedPatients ?? []).filter((p) => !p.shadow);
}

function collectGroupLeafStates(
  planId: string,
  group: InterventionGroupDto,
  states: Map<string, EconomicsLeafState>
): void {
  const groupId = group.id;
  if (!groupId) return;
  for (const ap of realSeats(group)) {
    const key = serializeKey({
      treatmentPlanId: planId,
      interventionGroupId: groupId,
      patientId: ap.patientId
    });
    states.set(key, {
      completed: ap.completed,
      reimbursementOverride: ap.reimbursementOverride ?? null,
      note: ap.reimbursementNote ?? null
    });
  }
}

// Builds the baseline leaf state from a server year plan: every patient assigned to every
// group across every plan, regardless of week (period filtering happens at render time).
export function buildLeafStates(yearPlan: YearPlanResponse): Map<string, EconomicsLeafState> {
  const states = new Map<string, EconomicsLeafState>();
  for (const plan of yearPlan.treatmentPlans) {
    const planId = plan.id;
    if (!planId) continue;
    for (const week of plan.weeks) {
      for (const group of week.groups ?? []) {
        collectGroupLeafStates(planId, group, states);
      }
    }
  }
  return states;
}

function collectPlanClaimStates(
  plan: TreatmentPlanDto,
  planId: string,
  states: Map<string, boolean>
): void {
  for (const week of plan.weeks) {
    for (const group of week.groups ?? []) {
      for (const ap of realSeats(group)) {
        const key = serializeClaimKey({ treatmentPlanId: planId, patientId: ap.patientId });
        if (!states.has(key)) states.set(key, false);
      }
    }
  }
  for (const patientId of plan.completionReimbursementClaimedPatientIds ?? []) {
    states.set(serializeClaimKey({ treatmentPlanId: planId, patientId }), true);
  }
}

// Server-recorded claim dates by claim key — the day each saved claim was made.
function buildClaimDates(yearPlan: YearPlanResponse): Map<string, string> {
  const dates = new Map<string, string>();
  for (const plan of yearPlan.treatmentPlans) {
    const planId = plan.id;
    if (!planId) continue;
    for (const claim of plan.completionReimbursementClaims ?? []) {
      dates.set(serializeClaimKey({ treatmentPlanId: planId, patientId: claim.patientId }), claim.claimedAt);
    }
  }
  return dates;
}

// Baseline claim state from a server year plan: one entry per (plan, patient) pair on plans
// that have a completion amount; true when the server lists the patient as claimed.
export function buildClaimStates(yearPlan: YearPlanResponse): Map<string, boolean> {
  const states = new Map<string, boolean>();
  for (const plan of yearPlan.treatmentPlans) {
    const planId = plan.id;
    if (!planId || plan.completionReimbursementAmount == null) continue;
    collectPlanClaimStates(plan, planId, states);
  }
  return states;
}

// Claim keys of every (plan, patient) pair whose groups on the plan are all completed. Counted
// across the whole year, never just the selected period, so narrowing the week filter cannot make
// a completion reimbursement claimable before the patient has finished every group.
export function fullyCompletedClaimKeys(leaves: Map<string, EconomicsLeafState>): Set<string> {
  const allCompleted = new Map<string, boolean>();
  for (const [key, state] of leaves) {
    const { treatmentPlanId, patientId } = parseKey(key);
    const claimKey = serializeClaimKey({ treatmentPlanId, patientId });
    allCompleted.set(claimKey, (allCompleted.get(claimKey) ?? true) && state.completed);
  }
  return new Set([...allCompleted].filter(([, isDone]) => isDone).map(([claimKey]) => claimKey));
}

// Sets completion across a set of leaves. Uncompleting also clears the override (mirrors the backend).
export function applyCompletion(
  leaves: Map<string, EconomicsLeafState>,
  keys: string[],
  completed: boolean
): Map<string, EconomicsLeafState> {
  const next = new Map(leaves);
  for (const key of keys) {
    const current = next.get(key);
    const keepsOverride = completed && current?.reimbursementOverride != null;
    next.set(key, {
      completed,
      reimbursementOverride: keepsOverride ? (current?.reimbursementOverride ?? null) : null,
      note: keepsOverride ? (current?.note ?? null) : null
    });
  }
  return next;
}

// Claims the completion reimbursement of every patient touched by `keys` who now has every group
// on the plan completed. Only plans with a completion amount have claim entries, so the rest are
// skipped. Never un-claims: a claim stays until the user removes it.
export function claimFullyCompleted(
  claims: Map<string, boolean>,
  leaves: Map<string, EconomicsLeafState>,
  keys: string[]
): Map<string, boolean> {
  const completedClaimKeys = fullyCompletedClaimKeys(leaves);
  const next = new Map(claims);
  for (const key of keys) {
    const { treatmentPlanId, patientId } = parseKey(key);
    const claimKey = serializeClaimKey({ treatmentPlanId, patientId });
    if (next.has(claimKey) && completedClaimKeys.has(claimKey)) next.set(claimKey, true);
  }
  return next;
}

function selectionFrom(completedFlags: boolean[]): SelectionState {
  if (completedFlags.length === 0 || completedFlags.every((c) => !c)) return 'none';
  if (completedFlags.every((c) => c)) return 'all';
  return 'partial';
}

function collectGroupNodes(
  planId: string,
  group: InterventionGroupDto,
  weekNumber: number,
  working: Map<string, EconomicsLeafState>,
  byPatient: Map<string, EconomicsGroupNode[]>
): void {
  const groupId = group.id;
  if (!groupId) return;
  const defaultAmount = group.reimbursementAmount ?? 0;
  for (const ap of realSeats(group)) {
    const key = serializeKey({
      treatmentPlanId: planId,
      interventionGroupId: groupId,
      patientId: ap.patientId
    });
    const state = working.get(key) ?? {
      completed: ap.completed,
      reimbursementOverride: ap.reimbursementOverride ?? null,
      note: ap.reimbursementNote ?? null
    };
    const node: EconomicsGroupNode = {
      key,
      treatmentPlanId: planId,
      interventionGroupId: groupId,
      patientId: ap.patientId,
      groupName: group.name ?? '',
      weekNumber,
      defaultAmount,
      completed: state.completed,
      reimbursementOverride: state.reimbursementOverride,
      note: state.note,
      effectiveAmount: state.reimbursementOverride ?? defaultAmount,
      isOverridden: state.reimbursementOverride !== null,
      completedAt: ap.completedAt ?? null
    };
    const list = byPatient.get(ap.patientId) ?? [];
    list.push(node);
    byPatient.set(ap.patientId, list);
  }
}

function collectPlanGroupNodes(
  plan: TreatmentPlanDto,
  planId: string,
  working: Map<string, EconomicsLeafState>,
  fromWeek: number,
  toWeek: number
): Map<string, EconomicsGroupNode[]> {
  const byPatient = new Map<string, EconomicsGroupNode[]>();
  for (const week of plan.weeks) {
    const weekNumber = week.weekNumber ?? 0;
    if (weekNumber < fromWeek || weekNumber > toWeek) continue;
    for (const group of week.groups ?? []) {
      collectGroupNodes(planId, group, weekNumber, working, byPatient);
    }
  }
  return byPatient;
}

function buildPatientNodes(
  byPatient: Map<string, EconomicsGroupNode[]>,
  planId: string,
  getPatientName: (patientId: string) => string
): EconomicsPatientNode[] {
  const patientNodes: EconomicsPatientNode[] = [];
  for (const [patientId, groups] of byPatient) {
    groups.sort((a, b) => a.weekNumber - b.weekNumber || a.groupName.localeCompare(b.groupName));
    patientNodes.push({
      patientId,
      treatmentPlanId: planId,
      patientName: getPatientName(patientId),
      groups,
      completionClaim: null,
      completedCount: groups.filter((g) => g.completed).length,
      total: groups.reduce((sum, g) => sum + (g.completed ? g.effectiveAmount : 0), 0),
      selectionState: selectionFrom(groups.map((g) => g.completed))
    });
  }
  patientNodes.sort((a, b) => a.patientName.localeCompare(b.patientName));
  return patientNodes;
}

// Everything the claim rows of one tree are built from, keyed by serialized claim key.
interface ClaimContext {
  working: Map<string, boolean>;
  server: Map<string, boolean>;
  claimableKeys: Set<string>;
  claimedAt: Map<string, string>;
}

function buildClaimNode(
  key: string,
  planId: string,
  patientId: string,
  amount: number,
  claims: ClaimContext
): EconomicsCompletionClaimNode {
  const claimed = claims.working.get(key) ?? false;
  const serverClaimed = claims.server.get(key) ?? false;
  return {
    key,
    treatmentPlanId: planId,
    patientId,
    amount,
    claimed,
    isClaimable: claims.claimableKeys.has(key),
    isUnsaved: claimed !== serverClaimed,
    claimedAt: claimed && serverClaimed ? (claims.claimedAt.get(key) ?? null) : null
  };
}

// Adds the completion-claim node to each patient when the plan has a completion amount. The
// claim changes only the totals, never the completed counts or selection states.
function attachCompletionClaims(
  patients: EconomicsPatientNode[],
  amount: number | undefined,
  planId: string,
  claims: ClaimContext
): void {
  if (amount == null) return;
  for (const patient of patients) {
    const key = serializeClaimKey({ treatmentPlanId: planId, patientId: patient.patientId });
    patient.completionClaim = buildClaimNode(key, planId, patient.patientId, amount, claims);
    if (patient.completionClaim.claimed) patient.total += amount;
  }
}

// A claimed patient can lose every seat on the plan (moved in the Gantt) or fall outside the
// week filter. Their claim must stay visible anyway: hindsight un-claiming is always possible,
// and the totals must keep counting money already claimed — so they get a groupless row.
function appendDetachedClaims(
  patients: EconomicsPatientNode[],
  amount: number | undefined,
  planId: string,
  claims: ClaimContext,
  getPatientName: (patientId: string) => string
): void {
  if (amount == null) return;
  const present = new Set(patients.map((p) => p.patientId));
  for (const [key, claimed] of claims.working) {
    const { treatmentPlanId, patientId } = parseClaimKey(key);
    if (treatmentPlanId !== planId || present.has(patientId)) continue;
    if (!claimed && !(claims.server.get(key) ?? false)) continue;
    patients.push({
      patientId,
      treatmentPlanId: planId,
      patientName: getPatientName(patientId),
      groups: [],
      completionClaim: buildClaimNode(key, planId, patientId, amount, claims),
      completedCount: 0,
      total: claimed ? amount : 0,
      selectionState: 'none'
    });
  }
  patients.sort((a, b) => a.patientName.localeCompare(b.patientName));
}

// Pivots the server data into a Plan -> Patient -> Group tree, filtered to [fromWeek, toWeek],
// using the local working state for completion/override and claims. Only actionable rows are kept.
export function buildTree(
  yearPlan: YearPlanResponse,
  working: Map<string, EconomicsLeafState>,
  workingClaims: Map<string, boolean>,
  serverClaims: Map<string, boolean>,
  fromWeek: number,
  toWeek: number,
  getPatientName: (patientId: string) => string
): EconomicsTreeModel {
  const planNodes: EconomicsPlanNode[] = [];
  let grandTotal = 0;
  let grandCompleted = 0;
  const claims: ClaimContext = {
    working: workingClaims,
    server: serverClaims,
    claimableKeys: fullyCompletedClaimKeys(working),
    claimedAt: buildClaimDates(yearPlan)
  };

  for (const plan of yearPlan.treatmentPlans) {
    const planId = plan.id;
    if (!planId) continue;

    const byPatient = collectPlanGroupNodes(plan, planId, working, fromWeek, toWeek);
    const patientNodes = buildPatientNodes(byPatient, planId, getPatientName);
    attachCompletionClaims(patientNodes, plan.completionReimbursementAmount, planId, claims);
    appendDetachedClaims(patientNodes, plan.completionReimbursementAmount, planId, claims, getPatientName);
    if (patientNodes.length === 0) continue;
    const planTotal = patientNodes.reduce((sum, p) => sum + p.total, 0);
    const planCompleted = patientNodes.reduce((sum, p) => sum + p.completedCount, 0);
    const planFlags = patientNodes.flatMap((p) => p.groups.map((g) => g.completed));
    planNodes.push({
      treatmentPlanId: planId,
      planName: plan.name,
      patients: patientNodes,
      completedCount: planCompleted,
      total: planTotal,
      selectionState: selectionFrom(planFlags)
    });
    grandTotal += planTotal;
    grandCompleted += planCompleted;
  }

  planNodes.sort((a, b) => a.planName.localeCompare(b.planName));
  return { plans: planNodes, grandTotal, completedCount: grandCompleted };
}

// Normalizes a leaf to its billable shape: a not-completed leaf carries no override,
// and without an override there is no amount for a note to explain.
function normalize(state: EconomicsLeafState): EconomicsLeafState {
  const reimbursementOverride = state.completed ? state.reimbursementOverride : null;
  return {
    completed: state.completed,
    reimbursementOverride,
    note: reimbursementOverride === null ? null : state.note
  };
}

// Produces the minimal set of changes between the server snapshot and the working state.
export function diffChanges(
  server: Map<string, EconomicsLeafState>,
  working: Map<string, EconomicsLeafState>
): EconomicsChangeEntry[] {
  const changes: EconomicsChangeEntry[] = [];
  for (const [key, workingState] of working) {
    const serverState = server.get(key);
    const w = normalize(workingState);
    const s = serverState ? normalize(serverState) : { completed: false, reimbursementOverride: null };
    if (w.completed === s.completed && w.reimbursementOverride === s.reimbursementOverride) continue;
    // The note travels with an amount change even when it ends at null: clearing an override back
    // to the group default still has to be explained, and the reason is the user's, not the diff's.
    changes.push({
      ...parseKey(key),
      completed: w.completed,
      reimbursementOverride: w.reimbursementOverride,
      note: noteFor(w, workingState)
    });
  }
  return changes;
}

// A cleared override drops its note in `normalize`, but the backend still demands a reason for the
// clearing itself — so fall back to the note the user typed in the dialog before it was cleared.
function noteFor(normalized: EconomicsLeafState, edited: EconomicsLeafState): string | null {
  return normalized.note ?? edited.note;
}

// Produces the minimal set of claim toggles between the server snapshot and the working state.
export function diffClaims(
  server: Map<string, boolean>,
  working: Map<string, boolean>
): CompletionClaimChange[] {
  const changes: CompletionClaimChange[] = [];
  for (const [key, claimed] of working) {
    if ((server.get(key) ?? false) === claimed) continue;
    changes.push({ ...parseClaimKey(key), claimed });
  }
  return changes;
}
